// frontend/src/hooks/useBetaFeatures.ts
import { useState, useEffect, useCallback } from 'react'
import { api } from '@/lib/api'

const LS_KEY = 'digestr_seen_features'

export interface BetaFeature {
    id: number
    feature_id: number
    key: string
    title: string
    description: string
    badge_text: string
    cta_label: string
    cta_url: string | null
}

function getLocalSeen(): Set<number> {
    try {
        const raw = localStorage.getItem(LS_KEY)
        return raw ? new Set(JSON.parse(raw)) : new Set()
    } catch {
        return new Set()
    }
}

function markLocalSeen(stateId: number) {
    const seen = getLocalSeen()
    seen.add(stateId)
    localStorage.setItem(LS_KEY, JSON.stringify([...seen]))
}

export function useBetaFeatures() {
    const [queue, setQueue] = useState<BetaFeature[]>([])
    const [current, setCurrent] = useState<BetaFeature | null>(null)

    useEffect(() => {
        const localSeen = getLocalSeen()

        api.get('/api/v1/feature-announcements/unseen')
            .then(res => {
                console.log('Beta features:', res)
                const unseen: BetaFeature[] = (res || []).filter(
                    (f: BetaFeature) => !localSeen.has(f.id)
                )
                setQueue(unseen)
                if (unseen.length > 0) setCurrent(unseen[0])
            })
            .catch(() => {
                // Silently fail — don't break the app if this endpoint is down
            })
    }, [])

    const dismiss = useCallback(async (optedIn = false) => {
        if (!current) return

        // Optimistic local update
        markLocalSeen(current.id)

        // Backend update (fire and forget)
        api.post(`/api/v1/feature-announcements/${current.id}/seen`, { opted_in: optedIn })
            .catch(() => { }) // Already marked locally

        // Advance queue
        setQueue(prev => {
            const remaining = prev.slice(1)
            setCurrent(remaining[0] ?? null)
            return remaining
        })
    }, [current])

    return { current, dismiss, queueLength: queue.length }
}